import { motion } from 'framer-motion';
import { type CSSProperties, type ReactNode } from 'react';

/**
 * TabPanel Component
 *
 * A wrapper component that displays the content of the active tab with
 * smooth animation transitions using Framer Motion.
 *
 * Features:
 * - Fade-in animation when content changes
 * - Full-width layout by default
 * - Custom styling support
 *
 * @example
 * ```tsx
 * <TabPanel>
 *   <div>Tab content goes here</div>
 * </TabPanel>
 * ```
 */

interface ITabPanelProps {
  /** The content to display in the tab panel */
  children: ReactNode;
  /** Custom styles applied to the panel container */
  style?: CSSProperties;
}

export const TabPanel = ({ children, style = {} }: ITabPanelProps) => (
  <motion.div
    initial={{ opacity: 0, y: 0 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    style={{
      width: '100%',
      ...style,
    }}
  >
    {children}
  </motion.div>
);

TabPanel.displayName = 'TabPanel';
